import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  Credentials,
  InviteUserPayload,
  OrganizationUser,
  RegisterPayload,
  ResetPasswordPayload,
  SignInResponse,
  UserInvite,
  UserPersonalData,
} from '../../types/auth';
import { EnvService } from '@shared/services/env.service';
import { catchError, throwError } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  readonly AUTH = `${this.envService.getApiUrl()}/auth`;
  readonly PASSWORD = `${this.envService.getApiUrl()}/password`;
  readonly USER = `${this.envService.getApiUrl()}/user`;
  constructor(
    private readonly envService: EnvService,
    private readonly http: HttpClient,
  ) {}

  refreshToken(token: string) {
    return this.http
      .post<SignInResponse>(`${this.AUTH}/refreshToken`, {
        token,
      })
      .pipe(
        catchError(err => {
          console.log(err);
          return throwError(() => err);
        }),
      );
  }

  resetPassword(payload: ResetPasswordPayload) {
    return this.http.post(`${this.PASSWORD}/reset`, payload, {
      responseType: 'text',
    });
  }

  sendResetRequest(email: string) {
    return this.http.post(`${this.PASSWORD}/requestReset`, email, {
      responseType: 'text',
    });
  }

  sendRegistrationRequest(email: string) {
    return this.http.post(`${this.USER}/sendVerificationLink`, null, {
      params: {
        email,
      },
      responseType: 'text',
    });
  }

  signIn(credentials: Credentials) {
    return this.http.post<SignInResponse>(`${this.AUTH}/signIn`, {
      email: credentials.login,
      password: credentials.password,
    });
  }

  register(payload: RegisterPayload) {
    return this.http.post<SignInResponse>(`${this.USER}/register`, payload);
  }

  changePassword(passwords: { newPassword: string; currentPassword: string }) {
    return this.http.post<SignInResponse>(`${this.PASSWORD}/change`, passwords);
  }

  passwordExists() {
    return this.http.get<boolean>(`${this.PASSWORD}/exists`);
  }

  passwordSetRequest() {
    return this.http.post(`${this.PASSWORD}/requestSet`, null, {
      responseType: 'text',
    });
  }

  invite(payload: InviteUserPayload) {
    return this.http.post<void>(`${this.USER}/invite`, payload);
  }

  getInviteDetails() {
    return this.http.get<UserInvite[]>(`${this.USER}/getInviteDetails`);
  }

  acceptInvite(divisionId: number) {
    return this.http.post<SignInResponse>(`${this.USER}/acceptInvite`, null, {
      params: {
        divisionId,
      },
    });
  }

  getUsersByDivisionId(divisionId: number) {
    return this.http.get<OrganizationUser[]>(`${this.USER}/getDetailsByDivisionId`, {
      params: {
        divisionId,
      },
    });
  }

  getPersonalData() {
    return this.http.get<UserPersonalData>(`${this.USER}/getPersonalData`);
  }

  savePersonalData(payload: UserPersonalData) {
    return this.http.post<void>(`${this.USER}/savePersonalData`, payload);
  }

  setActive(params: { userId: number; divisionId: number; active: boolean }) {
    return this.http.post(`${this.USER}/setActive`, null, {
      params,
    });
  }

  changeRole(params: { userId: number; divisionId: number; roleId: number }) {
    return this.http.post<void>(`${this.USER}/changeRole`, null, {
      params,
    });
  }

  deleteFromDivision(params: { userId: number; divisionId: number }) {
    return this.http.post<void>(`${this.USER}/deleteFromDivision`, null, {
      params,
    });
  }
}
